import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { createRoverTdm } from '@/api/Rover/applicationTDM';
import { DueDateInput } from '@/components/Projects/Project/DueDateInput';
import { SupportSelect } from '@/components/Projects/Project/SupportSelect';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Loading from '@/components/UI/loading';
import { TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import { useSherpaVendors } from '@/hooks/ReactQueryHooks/useVendorQuery';
import { notifyUser } from '@/store/Toast';
import type { UserOption } from '@/types/Constants';
import { sanitizeResponseError } from '@/utils';
import styles from '../Form.module.css';
import { validationSchema } from './validationSchema';

export default function NewTaskForm({
  phase,
  teams,
  formModalHandler,
}: {
  phase: { label: string; value: string };
  teams?: any;
  formModalHandler: (name: string) => void;
}) {
  const params = useParams<string>();
  const dispatch = useDispatch();
  const initialData = {
    name: '',
    lead: 0 as any,
    notes: undefined as string | null | undefined,
    vendor: undefined as any,
    support: undefined as any,
    startDate: undefined as string | null | undefined,
    dueDate: undefined as string | null | undefined,
  };
  const queryClient = useQueryClient();
  const handleAddSuccess = useCallback(() => {
    queryClient.invalidateQueries([TdmReactQueryKey.GET_APPLICATION_TASKS]);
    formModalHandler('newTask');
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'Task created successfully',
        toastType: 'success',
      })
    );
  }, [queryClient, dispatch, formModalHandler]);

  const addTask = useMutation((payload: any) => createRoverTdm({ type: 'tasks', payload }), {
    onSuccess: () => {
      handleAddSuccess();
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const fetchVendors = useSherpaVendors({});
  const vendors = fetchVendors?.data?.data;

  const onSubmit = async (values: any) => {
    const selectedData = {
      name: values.name,
      phase: phase.value,
      leadId: Number(values.lead.id),
      ...(values.notes && { notes: values.notes }),
      ...(values.support && { supportId: Number(values.support.id) }),
      ...(values.vendor && { vendorId: values.vendor.id }),
      ...(values.startDate && {
        startDate: dayjs(values.startDate).utc().format('MM/DD/YYYY'),
      }),
      ...(values.dueDate && {
        dueDate: dayjs(values.dueDate).utc().format('MM/DD/YYYY'),
      }),
      applicationId: params.applicationId,
    };
    addTask.mutate(selectedData);
  };
  return (
    <HookFormProvider defaultValues={initialData} resolver={yupResolver(validationSchema) as any} mode='all'>
      <HookForm noDefaultClass className='w-full' onSubmit={onSubmit}>
        {addTask.isLoading && <Loading loadingText='Updating Project' />}
        <h2 className={styles['form-title']}>New {phase.label} Task</h2>
        <div className='grid grid-cols-2 gap-5'>
          <HookInput type='text' label='Task Name' name='name' id='name' placeholder='e.g. SEIN Office' />
          <HookReactSingleSelect
            id='lead'
            options={teams}
            getOptionLabel={(option) => (option as UserOption).name}
            getOptionValue={(option) => (option as UserOption).id.toString()}
            placeholder='Select Lead'
            name='lead'
            label='Lead'
          />
          <HookInput type='date' label='Start Date' name='startDate' id='start-date' />
          <SupportSelect internalTeam={teams} />
          <DueDateInput />
          <HookReactSingleSelect
            id='vendor'
            options={vendors}
            getOptionLabel={(option) => (option as UserOption).name}
            getOptionValue={(option) => (option as UserOption).id.toString()}
            placeholder='Select Vendor'
            name='vendor'
            label='Vendor'
            isClearable
          />
          <div className='col-span-2'>
            <HookTextarea rows={3} label='Notes' name='notes' id='notes' placeholder='e.g. any example' />
          </div>
        </div>
        <div className='flex gap-4 justify-end mt-4'>
          <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={() => formModalHandler('newTask')} />
          <Button label='Create' btnStyle='btn-primary' type='submit' />
        </div>
      </HookForm>
    </HookFormProvider>
  );
}
